import apiClient from '../api/apiClient';
import { AxiosResponse } from 'axios';
import { setAuthToken, removeAuthToken } from '../utils/authToken';

const userBaseURL = '/api/users';

/** Matches Prisma `Item` row fields returned on `UserSettings.title` / `.avatar`. */
export type ItemType = 'TITLE' | 'BACKGROUND' | 'AVATAR' | 'COLORPALETTE';
export type ItemRarity = 'BASIC' | 'COMMON' | 'UNCOMMON' | 'RARE' | 'MYSTIC' | 'SOVEREIGN';

export interface UserSettingsItem {
    id: number;
    type: ItemType;
    rarity: ItemRarity;
    shopName: string;
    description: string | null;
    fileName: string;
}

// Types matching backend UserService
export interface UserSettings {
    id: number;
    userId: number;
    /** 0–100; 0 = muted */
    music: number;
    /** 0–100; 0 = muted */
    sfx: number;
    color: string;
    titleId: number | null;
    backgroundId: number | null;
    avatarId: number | null;
    colorPaletteId: number | null;
    title: UserSettingsItem | null;
    avatar: UserSettingsItem | null;
}

/** Matches backend UserRole enum */
export type UserRole = 'ADMIN' | 'USER';

export interface User {
    id: number;
    email: string | null;
    username: string;
    role: UserRole;
    mmr: number;
    settings: UserSettings | null;
}

// Types matching backend request bodies
export interface LoginRequest {
    email: string;
    password: string;
}

export interface LoginResponse {
    user: User;
    token: string;
}

export interface RegisterRequest {
    email: string;
    username: string;
    password: string;
}

export interface RegisterResponse {
    user: User;
    token: string;
}

/** Response from POST /auth/guest-session (backend-issued guest identity) */
export interface GuestSessionResponse {
    id: string;       // guestId
    username: string;
    isGuest: true;
    token: string;
}

export interface UpdateUserRequest {
    user: {
        username?: string;
        mmr?: number;
        /** 0–100; 0 = muted */
        music?: number;
        /** 0–100; 0 = muted */
        sfx?: number;
        color?: string;
        titleId?: number | null;
        backgroundId?: number | null;
        avatarId?: number | null;
        colorPaletteId?: number | null;
    };
}

class UserService {
    /**
     * Get user by ID
     * Note: This endpoint may not exist in the backend yet
     */
    get(id: number): Promise<AxiosResponse<User>> {
        return apiClient.get<User>(`${userBaseURL}/${id}`);
    }

    /**
     * Login user
     * POST /auth/login
     * Request: { email: string, password: string }
     * Response: { user: User, token: string }
     * 
     * Automatically stores the JWT token for future authenticated requests
     */
    async login(info: LoginRequest): Promise<AxiosResponse<LoginResponse>> {
        const response = await apiClient.post<LoginResponse>('/auth/login', info);
        
        // Store the JWT token for future requests
        if (response.data.token) {
            setAuthToken(response.data.token);
        }
        
        return response;
    }

    /**
     * Register new user
     * POST /auth/register
     * Request: { email: string, username: string, password: string }
     * Response: { user: User, token: string }
     * 
     * Automatically stores the JWT token for future authenticated requests
     */
    async register(info: RegisterRequest): Promise<AxiosResponse<RegisterResponse>> {
        const response = await apiClient.post<RegisterResponse>('/auth/register', info);
        
        // Store the JWT token for future requests
        if (response.data.token) {
            setAuthToken(response.data.token);
        }
        
        return response;
    }

    /**
     * Update current user (JWT identifies the user)
     * PUT /api/users/me
     * Request: { user: { ...updateFields } }
     * Response: User
     */
    update(user: UpdateUserRequest['user']): Promise<AxiosResponse<User>> {
        return apiClient.put<User>(`${userBaseURL}/me`, { user });
    }

    /**
     * Delete current user (JWT identifies the user)
     * DELETE /api/users/me
     * Response: User
     */
    delete(): Promise<AxiosResponse<User>> {
        return apiClient.delete<User>(`${userBaseURL}/me`);
    }

    /**
     * Create a guest session (no auth required).
     * POST /auth/guest-session
     * Returns { id, username, isGuest, token }. Caller should store token and build guest user.
     */
    createGuestSession(): Promise<AxiosResponse<GuestSessionResponse>> {
        return apiClient.post<GuestSessionResponse>('/auth/guest-session');
    }

    /**
     * Reissue a guest JWT for the same guestId (token may be expired).
     * POST /auth/guest-session/refresh — body `{ token }` only; does not set storage (AuthProvider does).
     */
    refreshGuestSession(token: string): Promise<AxiosResponse<GuestSessionResponse>> {
        return apiClient.post<GuestSessionResponse>('/auth/guest-session/refresh', { token });
    }

    /**
     * Logout - clears the stored auth token only.
     * Use AuthProvider.logout() to clear and immediately obtain a new guest session.
     */
    logout(): void {
        removeAuthToken();
    }
}

export default new UserService();

